import React, { useState, useEffect, useRef } from 'react';
import { jsPDF } from 'jspdf';
import { VdpTemplate, VdpLayout, Unit, CSVData } from '../../types';
import { 
  Eye, 
  X, 
  Download, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldCheck, 
  Scissors, 
  Ruler, 
  ArrowRight, 
  RefreshCw, 
  ZoomIn, 
  ZoomOut, 
  Layers
} from 'lucide-react';
import { formatUnit } from '../../utils/units';
import { MacWindow } from './MacWindow';

interface LivePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: VdpTemplate;
  layout: VdpLayout;
  appUnit: Unit;
  csvData: CSVData | null;
  previewRecordIndex: number;
  onProceedToImposition?: () => void;
  onUpdateBleed?: (bleedMm: number) => void;
}

export const LivePreviewModal: React.FC<LivePreviewModalProps> = ({
  isOpen,
  onClose,
  template,
  layout,
  appUnit,
  csvData,
  previewRecordIndex,
  onProceedToImposition,
  onUpdateBleed
}) => {
  const [showTrimBox, setShowTrimBox] = useState<boolean>(true);
  const [showBleedBox, setShowBleedBox] = useState<boolean>(true);
  const [showSafeZone, setShowSafeZone] = useState<boolean>(true);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [selectedBleedMm, setSelectedBleedMm] = useState<number>(layout.bleed || 2);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  // Sync selected bleed with prop
  useEffect(() => {
    setSelectedBleedMm(layout.bleed || 2);
  }, [layout.bleed]);

  // Convert template dimensions to pt and mm
  const widthPt = Number(template.width) || 250;
  const heightPt = Number(template.height) || 150;
  
  // Convert bleed from mm to pt (1 mm = 2.83465 pt)
  const bleedPt = selectedBleedMm * 2.83465;
  const totalWidthPt = widthPt + bleedPt * 2;
  const totalHeightPt = heightPt + bleedPt * 2;

  const widthInUnit = formatUnit(widthPt, appUnit);
  const heightInUnit = formatUnit(heightPt, appUnit);
  const totalWidthInUnit = formatUnit(totalWidthPt, appUnit);
  const totalHeightInUnit = formatUnit(totalHeightPt, appUnit);

  // Prepress Validation Rules
  const safeMarginPt = 8.5; // ~3mm safe margin inside trim
  const elementsOutsideSafeZone = template.elements.filter(el => {
    return el.x < safeMarginPt || el.y < safeMarginPt || (el.x + el.width) > (widthPt - safeMarginPt) || (el.y + el.height) > (heightPt - safeMarginPt);
  });

  // Canvas Rendering for live 150 DPI preview
  const drawPreviewCanvas = () => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Determine scale for crisp rendering
    const maxDisplayWidth = 720;
    const scale = Math.min(2.5, maxDisplayWidth / Math.max(totalWidthPt, 100));
    const canvasW = totalWidthPt * scale;
    const canvasH = totalHeightPt * scale;

    canvas.width = canvasW;
    canvas.height = canvasH;

    // Reset transform
    ctx.clearRect(0, 0, canvasW, canvasH);
    ctx.save();
    ctx.scale(scale, scale);

    // 1. Fill entire printable area
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, totalWidthPt, totalHeightPt);

    // 2. Bleed Area Tint (Rose)
    if (selectedBleedMm > 0 && showBleedBox) {
      ctx.fillStyle = '#fff1f2'; // rose-50
      ctx.fillRect(0, 0, totalWidthPt, totalHeightPt);

      // Inner Trim Area (White)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(bleedPt, bleedPt, widthPt, heightPt);

      // Bleed Outer Box Line
      ctx.strokeStyle = '#3b82f6'; // blue-500
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(0, 0, totalWidthPt, totalHeightPt);
      ctx.setLineDash([]);
    }

    // 3. Render Template Elements
    template.elements.forEach(el => {
      if (el.disabled || el.isSkipped) return;

      const elX = bleedPt + el.x;
      const elY = bleedPt + el.y;
      const elW = el.width || 50;
      const elH = el.height || 50;

      ctx.save();
      if (el.rotation) {
        const cx = elX + elW / 2;
        const cy = elY + elH / 2;
        ctx.translate(cx, cy);
        ctx.rotate((el.rotation * Math.PI) / 180);
        ctx.translate(-cx, -cy);
      }

      if (['text', 'uppercase', 'currency', 'date', 'serial'].includes(el.type)) {
        let textVal = el.content || el.text || 'Text';
        if (csvData && csvData.rows.length > previewRecordIndex && el.field) {
          textVal = String(csvData.rows[previewRecordIndex][el.field] ?? textVal);
        }
        if (el.type === 'uppercase') textVal = textVal.toUpperCase();

        const fontSize = el.fontSize || 14;
        ctx.font = `${el.isBold ? 'bold' : 'normal'} ${fontSize}px ${el.fontFamily || 'sans-serif'}`;
        ctx.fillStyle = el.color || '#000000';
        ctx.textAlign = (el.textAlign as CanvasTextAlign) || 'left';
        ctx.textBaseline = 'top';

        let textX = elX;
        if (el.textAlign === 'center') textX = elX + elW / 2;
        if (el.textAlign === 'right') textX = elX + elW;

        ctx.fillText(textVal, textX, elY, elW);
      } else if (el.type === 'image') {
        const imageSrc = el.src || el.staticValue;
        if (imageSrc) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            ctx.drawImage(img, elX, elY, elW, elH);
          };
          img.src = imageSrc;
          if (img.complete) {
            ctx.drawImage(img, elX, elY, elW, elH);
          }
        } else {
          ctx.fillStyle = '#e2e8f0';
          ctx.fillRect(elX, elY, elW, elH);
          ctx.strokeStyle = '#94a3b8';
          ctx.strokeRect(elX, elY, elW, elH);
        }
      } else if (el.type === 'rectangle') {
        const radius = el.cornerRadius || el.borderRadius || 0;
        ctx.beginPath();
        if (radius > 0 && typeof ctx.roundRect === 'function') {
          ctx.roundRect(elX, elY, elW, elH, radius);
        } else {
          ctx.rect(elX, elY, elW, elH);
        }
        if (el.fillColor && el.fillColor !== 'transparent') {
          ctx.fillStyle = el.fillColor;
          ctx.fill();
        }
        if (el.strokeColor && el.strokeColor !== 'transparent') {
          ctx.strokeStyle = el.strokeColor;
          ctx.lineWidth = el.strokeWidth || 1;
          ctx.stroke();
        }
      } else if (el.type === 'circle') {
        const rx = elW / 2;
        const ry = elH / 2;
        const cx = elX + rx;
        const cy = elY + ry;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, 2 * Math.PI);
        if (el.fillColor && el.fillColor !== 'transparent') {
          ctx.fillStyle = el.fillColor;
          ctx.fill();
        }
        if (el.strokeColor && el.strokeColor !== 'transparent') {
          ctx.strokeStyle = el.strokeColor;
          ctx.lineWidth = el.strokeWidth || 1;
          ctx.stroke();
        }
      } else if (['qr', 'datamatrix'].includes(el.type)) {
        // QR 2D Matrix simulated pattern
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(elX, elY, elW, elH);
        ctx.fillStyle = '#000000';
        ctx.fillRect(elX + 2, elY + 2, elW - 4, elH - 4);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(elX + 6, elY + 6, elW - 12, elH - 12);
        ctx.fillStyle = '#000000';
        ctx.fillRect(elX + 10, elY + 10, elW - 20, elH - 20);
      } else if (['barcode', 'ean13', 'upca'].includes(el.type)) {
        // Simulated 1D Barcode lines
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(elX, elY, elW, elH);
        ctx.fillStyle = '#000000';
        const barWidth = 2;
        for (let bx = elX + 4; bx < elX + elW - 4; bx += barWidth * 2) {
          ctx.fillRect(bx, elY + 4, barWidth, elH - 14);
        }
        ctx.font = '8px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(el.content || el.text || '123456789', elX + elW / 2, elY + elH - 2);
      }

      ctx.restore();
    });

    // 4. Draw Trim Box (Cut Line - Red Dashed)
    if (showTrimBox) {
      ctx.strokeStyle = '#ef4444'; // red-500
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(bleedPt, bleedPt, widthPt, heightPt);
      ctx.setLineDash([]);
    }

    // 5. Draw Safe Zone Box (Clearance Line - Emerald Dashed)
    if (showSafeZone) {
      ctx.strokeStyle = '#10b981'; // emerald-500
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(
        bleedPt + safeMarginPt, 
        bleedPt + safeMarginPt, 
        widthPt - safeMarginPt * 2, 
        heightPt - safeMarginPt * 2
      );
      ctx.setLineDash([]);
    }

    // 6. Prepress Label Overlay
    ctx.fillStyle = 'rgba(15, 23, 42, 0.7)';
    ctx.fillRect(0, totalHeightPt - 14, totalWidthPt, 14);
    ctx.fillStyle = '#94a3b8';
    ctx.font = '8px monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(` PREPRESS PREVIEW • ${widthInUnit} x ${heightInUnit} ${appUnit} • BLEED: ${selectedBleedMm}mm`, 4, totalHeightPt - 7);

    ctx.restore();
  };

  useEffect(() => {
    if (isOpen) {
      drawPreviewCanvas();
    }
  }, [isOpen, template, selectedBleedMm, appUnit, previewRecordIndex, showTrimBox, showBleedBox, showSafeZone]);

  // Handle local bleed change
  const handleBleedChange = (newBleedMm: number) => {
    setSelectedBleedMm(newBleedMm);
    if (onUpdateBleed) {
      onUpdateBleed(newBleedMm);
    }
  };

  // Download Low-Res PDF Preview
  const handleDownloadPreviewPdf = () => {
    const isLandscape = totalWidthPt > totalHeightPt;
    const pdf = new jsPDF({
      orientation: isLandscape ? 'landscape' : 'portrait',
      unit: 'pt',
      format: [totalWidthPt, totalHeightPt]
    });

    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, totalWidthPt, totalHeightPt, 'F');

    // Bleed guide line
    pdf.setDrawColor(239, 68, 68);
    pdf.setLineWidth(0.5);
    pdf.rect(bleedPt, bleedPt, widthPt, heightPt, 'S');

    template.elements.forEach(el => {
      const elX = bleedPt + el.x;
      const elY = bleedPt + el.y;
      if (el.type === 'text') {
        pdf.setTextColor(el.color || '#000000');
        pdf.setFontSize(el.fontSize || 12);
        pdf.text(el.content || '', elX, elY + (el.fontSize || 12) * 0.8);
      }
    });

    pdf.save(`sticker_live_preview_${widthInUnit}x${heightInUnit}_150dpi.pdf`);
  };

  if (!isOpen) return null;

  return (
    <MacWindow isOpen={isOpen} onClose={onClose} title="Live PDF Prepress Preview" maxWidth="max-w-5xl" className="h-[90vh]">
      <div className="flex flex-col w-full h-full overflow-hidden">
        
        {/* Header Bar */}
        <div className="p-4 bg-zinc-950 border-b border-zinc-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
              <Eye className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-zinc-100">Live PDF Prepress Preview</h2>
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-full">
                  150 DPI Fast Thumbnail
                </span>
              </div>
              <p className="text-xs text-zinc-400">
                Verify target dimensions, printable bleed margins, and safe clearance before imposition.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadPreviewPdf}
              className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <Download className="w-3.5 h-3.5 text-blue-400" />
              <span>Download Low-Res PDF</span>
            </button>

            {onProceedToImposition && (
              <button
                onClick={() => {
                  onClose();
                  onProceedToImposition();
                }}
                className="px-4 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold text-xs rounded-lg shadow-md flex items-center gap-2 transition-all cursor-pointer"
              >
                <span>Proceed to Imposition</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}

            <button
              onClick={onClose}
              className="p-1.5 text-zinc-400 hover:text-zinc-100 rounded-lg hover:bg-zinc-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Main Body */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
          
          {/* Left Column: Interactive Controls & Verification Checklist */}
          <div className="w-full md:w-80 bg-zinc-950/80 border-r border-zinc-800 p-4 overflow-y-auto space-y-5 shrink-0">
            
            {/* Dimensions Card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3.5 space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold text-zinc-200">
                <Ruler className="w-4 h-4 text-blue-400" />
                <span>Sticker Dimensions</span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-zinc-950 p-2 rounded-lg border border-zinc-800/80">
                  <span className="text-[10px] text-zinc-500 block uppercase font-medium">Width</span>
                  <span className="font-mono text-zinc-100 font-semibold">{widthInUnit} {appUnit}</span>
                  <span className="text-[10px] text-zinc-500 block font-mono">({Math.round(widthPt)} pt)</span>
                </div>
                <div className="bg-zinc-950 p-2 rounded-lg border border-zinc-800/80">
                  <span className="text-[10px] text-zinc-500 block uppercase font-medium">Height</span>
                  <span className="font-mono text-zinc-100 font-semibold">{heightInUnit} {appUnit}</span>
                  <span className="text-[10px] text-zinc-500 block font-mono">({Math.round(heightPt)} pt)</span>
                </div>
              </div>
            </div>

            {/* Bleed Settings Verification */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3.5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-zinc-200">
                  <Scissors className="w-4 h-4 text-red-400" />
                  <span>Bleed Margin</span>
                </div>
                <span className="text-xs font-mono font-bold text-red-400">{selectedBleedMm} mm</span>
              </div>

              <div>
                <label className="text-[11px] text-zinc-400 block mb-1.5">Quick Adjust Bleed Margin</label>
                <div className="grid grid-cols-5 gap-1">
                  {[0, 1, 2, 3, 5].map(mm => (
                    <button
                      key={mm}
                      onClick={() => handleBleedChange(mm)}
                      className={`py-1.5 text-xs font-mono font-semibold rounded-lg border transition-all cursor-pointer ${
                        selectedBleedMm === mm 
                          ? 'bg-red-500/20 text-red-300 border-red-500/50 shadow-sm' 
                          : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:bg-zinc-800'
                      }`}
                    >
                      {mm}mm
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-2.5 bg-zinc-950 rounded-lg border border-zinc-800/80 text-[11px] text-zinc-400 space-y-1">
                <div className="flex justify-between">
                  <span>Total Printable Width:</span>
                  <span className="font-mono text-zinc-200 font-semibold">{totalWidthInUnit} {appUnit}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Printable Height:</span>
                  <span className="font-mono text-zinc-200 font-semibold">{totalHeightInUnit} {appUnit}</span>
                </div>
              </div>
            </div>

            {/* Overlays Visibility Toggles */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3.5 space-y-2.5">
              <div className="flex items-center gap-2 text-xs font-bold text-zinc-200 mb-1">
                <Layers className="w-4 h-4 text-purple-400" />
                <span>Prepress Overlays</span>
              </div>

              <label className="flex items-center justify-between text-xs text-zinc-300 cursor-pointer p-1.5 hover:bg-zinc-800/60 rounded-lg transition-colors">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                  <span>Trim Box (Cut Line)</span>
                </div>
                <input 
                  type="checkbox" 
                  checked={showTrimBox} 
                  onChange={e => setShowTrimBox(e.target.checked)}
                  className="rounded accent-red-500" 
                />
              </label>

              <label className="flex items-center justify-between text-xs text-zinc-300 cursor-pointer p-1.5 hover:bg-zinc-800/60 rounded-lg transition-colors">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  <span>Bleed Box (Media Area)</span>
                </div>
                <input 
                  type="checkbox" 
                  checked={showBleedBox} 
                  onChange={e => setShowBleedBox(e.target.checked)}
                  className="rounded accent-blue-500" 
                />
              </label>

              <label className="flex items-center justify-between text-xs text-zinc-300 cursor-pointer p-1.5 hover:bg-zinc-800/60 rounded-lg transition-colors">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                  <span>Safe Zone (~3mm clearance)</span>
                </div>
                <input 
                  type="checkbox" 
                  checked={showSafeZone} 
                  onChange={e => setShowSafeZone(e.target.checked)}
                  className="rounded accent-emerald-500" 
                />
              </label>
            </div>

            {/* Prepress Checklist Status */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3.5 space-y-2.5">
              <div className="flex items-center gap-2 text-xs font-bold text-zinc-200">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>Prepress Pre-Flight Check</span>
              </div>

              <div className="space-y-1.5 text-[11px]">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                  <span>Valid sticker aspect ratio</span>
                </div>
                
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                  <span>Bleed padding configured ({selectedBleedMm}mm)</span>
                </div>

                {elementsOutsideSafeZone.length === 0 ? (
                  <div className="flex items-center gap-2 text-emerald-400">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    <span>All elements clear safe margin</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-amber-400">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span>{elementsOutsideSafeZone.length} element(s) near trim edge</span>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Column: Visual Preview Canvas */}
          <div className="flex-1 bg-zinc-950 p-4 sm:p-6 flex flex-col items-center justify-center relative overflow-hidden">
            
            {/* Top Canvas Controls */}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10 pointer-events-none">
              <div className="flex items-center gap-2 bg-zinc-900/90 border border-zinc-800 px-3 py-1.5 rounded-xl shadow-lg backdrop-blur-md pointer-events-auto">
                <button
                  onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.2))}
                  className="p-1 hover:text-white text-zinc-400 rounded transition-colors"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-3.5 h-3.5" />
                </button>
                <span className="text-xs font-mono font-bold text-zinc-200 min-w-12 text-center">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <button
                  onClick={() => setZoomLevel(prev => Math.min(3, prev + 0.2))}
                  className="p-1 hover:text-white text-zinc-400 rounded transition-colors"
                  title="Zoom In"
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                </button>
                <div className="w-px h-3 bg-zinc-800"></div>
                <button
                  onClick={() => setZoomLevel(1)}
                  className="text-[10px] text-zinc-400 hover:text-zinc-200 font-semibold px-1.5 py-0.5 rounded hover:bg-zinc-800 transition-colors"
                >
                  Reset
                </button>
              </div>

              <div className="flex items-center gap-2 bg-zinc-900/90 border border-zinc-800 px-3 py-1.5 rounded-xl shadow-lg backdrop-blur-md pointer-events-auto">
                <button
                  onClick={drawPreviewCanvas}
                  className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-zinc-100 cursor-pointer"
                >
                  <RefreshCw className="w-3.5 h-3.5 text-blue-400" />
                  <span>Redraw Canvas</span>
                </button>
              </div>
            </div>

            {/* Embedded Live PDF Canvas / Visual Stage */}
            <div className="w-full h-full flex items-center justify-center overflow-auto p-8 relative">
              <div 
                className="relative transition-transform duration-150 shadow-2xl rounded-lg overflow-hidden border border-zinc-800 bg-zinc-900 flex items-center justify-center p-4"
                style={{ transform: `scale(${zoomLevel})` }}
              >
                <canvas 
                  ref={previewCanvasRef} 
                  className="rounded shadow-xl max-w-full max-h-[60vh] object-contain"
                />

                {/* Prepress Overlay Mark Legend floating on bottom right */}
                <div className="absolute bottom-3 right-3 bg-zinc-950/90 border border-zinc-800 p-2.5 rounded-xl text-[10px] text-zinc-300 space-y-1 backdrop-blur-md shadow-xl pointer-events-none">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-0.5 bg-red-500 border border-red-500"></div>
                    <span>Trim Box ({widthInUnit} x {heightInUnit} {appUnit})</span>
                  </div>
                  {selectedBleedMm > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-blue-500 border border-blue-500"></div>
                      <span>Bleed Box ({selectedBleedMm}mm Bleed)</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-0.5 bg-emerald-500 border border-emerald-500"></div>
                    <span>Safe Zone (Clearance)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Information */}
            <div className="absolute bottom-3 left-4 text-[11px] text-zinc-500 font-mono">
              Mode: On-The-Fly PDF Thumbnail • Resolution: 150 DPI • Target: Sticker Preview
            </div>

          </div>

        </div>

      </div>
    </MacWindow>
  );
};

